import { io, Socket } from "socket.io-client";
import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useAuthStore } from "./auth";

let socket: Socket | null = null;

export function getSocket(): Socket {
  if (socket === null) {
    socket = io(window.location.origin, {
      transports: ["websocket", "polling"],
      autoConnect: false,
    });
  }
  return socket;
}

export type ConnectionState = "connected" | "connecting" | "disconnected";

export interface PriceUpdateEvent {
  tripId: string;
  tripName: string;
  routeId: string;
  origin: string;
  destination: string;
  flightsFound: number;
  lowestPrice: number | null;
  previousLowest: number | null;
  timestamp: string;
}

export interface SearchLogEvent {
  routeId: string;
  status: string;
  flightsFound: number;
  durationMs: number;
  timestamp: string;
}

export function useSocket() {
  const [state, setState] = useState<ConnectionState>("disconnected");
  const token = useAuthStore((s) => s.token);
  const queryClient = useQueryClient();

  useEffect(() => {
    if (token === null) return;

    const s = getSocket();

    s.on("connect", () => {
      setState("connected");
      try {
        const payload = JSON.parse(atob(token.split(".")[1]));
        const userId = payload.sub || payload.user_id || payload.id;
        if (userId) s.emit("join", userId);
      } catch {
        // ignore decode errors
      }
    });

    s.on("disconnect", () => setState("disconnected"));
    s.on("reconnect_attempt", () => setState("connecting"));

    s.on("price:update", (data: PriceUpdateEvent) => {
      // The redesigned trip page reads ["tripPlan"]/["itinerary"]; the legacy page
      // read ["trip"]/["flights"]. Invalidate ALL so fares appear live, no refresh.
      queryClient.invalidateQueries({ queryKey: ["tripPlan", data.tripId] });
      queryClient.invalidateQueries({ queryKey: ["itinerary", data.tripId] });
      queryClient.invalidateQueries({ queryKey: ["trip", data.tripId] });
      queryClient.invalidateQueries({ queryKey: ["flights", data.routeId] });
      queryClient.invalidateQueries({ queryKey: ["trips"] });
    });

    s.connect();

    return () => {
      s.off("connect");
      s.off("disconnect");
      s.off("reconnect_attempt");
      s.off("price:update");
      s.disconnect();
    };
  }, [token, queryClient]);

  return state;
}
